import React, { FunctionComponent } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next'

import TimelineIcon from '@mui/icons-material/Timeline';

import {
  Box,
  ButtonGroup,
  Grid,
  Paper,
} from '@mui/material';
import Button from '@mui/material/Button';
import {
  Switcher,
  Typography
} from '../../../../components/v2/styled';

import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

// Timeline widget settings. Object under one value key (`timeline`):
//   { cta: boolean, amount: number }
// - `cta`   : on/off for the top-right goto button. On by default.
// - `amount`: how many timeline entries the widget shows — 5 (default),
//             7 or 10, rendered as a 3-button ButtonGroup. No per-row
//             visibility settings and no order setting.

export type TimelineSettingsValue = {
  cta?: boolean;
  amount?: number;
};

type Props = {
  value: TimelineSettingsValue;
  onChange: (v: TimelineSettingsValue) => void;
  // Unused for this widget (no order setting). Kept in the shared
  // SettingsProps shape so the dialog renders it uniformly.
  order?: string[];
  onOrderChange?: (v: string[]) => void;
};

const LABEL_BASE = 'coach.personal_settings.userdetail_widgets.settings.timeline';

export const CoachSettingsWidgetTimeline: FunctionComponent<Props> = ({
  value,
  onChange
}) => {
  const theme = useTheme();
  const {t} = useTranslation(['translations']);

  // Defensive defaults so the dialog works before the first save.
  const cta = HelpersClientWidgets.getTimelineCtaEnabled(value);
  const amount = HelpersClientWidgets.getTimelineAmount(value);

  return <Grid container spacing={2}>
    <Grid item xs={12}>
      <Typography variant="caption" sx={{color: 'text.secondary'}}>
        {t(`${LABEL_BASE}.description`)}
      </Typography>
    </Grid>

    {/* Widget-level settings. */}
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{color: 'text.secondary'}}>
        {t(`${LABEL_BASE}.options.title`)}
      </Typography>
      <Paper variant="outlined" sx={{padding: 1, marginTop: 0.5}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, padding: 1, borderRadius: 0.5, marginTop: 0.5}}>
          <HelpersClientWidgets.timelineCtaIcon
            sx={{fontSize: '1.2rem', color: cta ? theme.palette.primary.main : theme.palette.secondary.main}}
          />
          <Switcher
            label={t(`${LABEL_BASE}.options.cta`)}
            value={cta}
            greying
            onChange={(c) => onChange({ ...value, cta: c })}
          />
        </Box>

        {/* Amount picker: a 3-button ButtonGroup (selected = contained, the
            CourseEdit pattern). Persisted as the `amount` key; unknown values
            fall back to 5 via the helper getter. Label + group on separate
            lines (flex-wrap with a full-width label). */}
        <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1, rowGap: 0.5, padding: 1, borderRadius: 0.5, marginTop: 0.5}}>
          <Typography variant="body2" sx={{color: 'text.secondary', width: '100%'}}>
            {t(`${LABEL_BASE}.options.amount`)}
          </Typography>
          <ButtonGroup>
            {HelpersClientWidgets.timelineAmounts.map((a) => {
              const selected = amount === a.key;
              return <Button
                key={a.key}
                variant={selected ? "contained" : "outlined"}
                onClick={() => onChange({ ...value, amount: a.key })}
              >
                {a.key}
              </Button>;
            })}
          </ButtonGroup>
        </Box>
      </Paper>
    </Grid>
  </Grid>;
}

// Read-only status: the CTA toggle icon coloured by on/off + the timeline
// mark (primary) so the placed box summarises the config at a glance.
type StatusProps = {
  value: TimelineSettingsValue;
  order?: string[];
};

export const CoachSettingsWidgetTimelineStatus: FunctionComponent<StatusProps> = ({
  value
}) => {
  const theme = useTheme();
  const {t} = useTranslation(['translations']);

  const cta = HelpersClientWidgets.getTimelineCtaEnabled(value);
  const amount = HelpersClientWidgets.getTimelineAmount(value);

  return <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5, marginTop: 1}}>
    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5}}>
      <Box title={t(`${LABEL_BASE}.options.cta`)}>
        <HelpersClientWidgets.timelineCtaIcon
          sx={{ fontSize: '1.1rem', color: cta ? theme.palette.primary.main : theme.palette.secondary.main }}
        />
      </Box>
      <Box title={`${t(`${LABEL_BASE}.options.amount`)}: ${amount}`}>
        <TimelineIcon sx={{ fontSize: '1.1rem', color: theme.palette.primary.main }} />
      </Box>
    </Box>
  </Box>;
}
